import { type ReactNode } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Icon } from '@socialchorus/shared-ui-components';
import { useProgram } from '../../../../common/use-program';
import { MAX_IMAGES, MAX_IMAGE_SIZE_MB } from './use-image-uploader';
import { MAX_VIDEOS } from './use-video-uploader';
import { MAX_FILES, MAX_FILE_SIZE_MB } from './use-file-uploader';
import './dropzone.scss';

const DEFAULT_MAX_VIDEO_SIZE_MB = 300;

type DropzoneProps = {
  accept?: string;
  maxSize?: number;
  maxFiles?: number;
  onDropAccepted: (files: File[]) => void;
  onDropRejected: (rejections: FileRejection[]) => void;
  children: ReactNode;
};

const Dropzone = ({
  accept,
  maxSize,
  maxFiles,
  onDropAccepted,
  onDropRejected,
  children,
}: DropzoneProps) => {
  const { t } = useTranslation();
  const program = useProgram();
  const maxVideoSizeMB = Math.round(
    (Number(program.max_video_upload_size_in_bytes) ||
      DEFAULT_MAX_VIDEO_SIZE_MB * 1024 * 1024) /
      (1024 * 1024)
  );
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept,
    maxSize,
    maxFiles,
    multiple: true,
    noClick: true,
    onDropAccepted,
    onDropRejected,
  });

  return (
    <div
      {...getRootProps()}
      role={undefined}
      tabIndex={undefined}
      className="post-form__content"
    >
      <input {...getInputProps()} />
      {isDragActive && (
        <div className="post-media-drag-overlay">
          <Icon size={42}>add</Icon>
          <span className="text-subheading">
            {t('user_generated_content.media.drag.drop_files')}
          </span>
          <span className="text-caption">
            {t('user_generated_content.media.drag.drop_files_hint_images', {
              max: MAX_IMAGES,
              maxSizeMB: MAX_IMAGE_SIZE_MB,
            })}
          </span>
          <span className="text-caption">
            {t('user_generated_content.media.drag.drop_files_hint_videos', {
              max: MAX_VIDEOS,
              maxSizeMB: maxVideoSizeMB,
            })}
          </span>
          <span className="text-caption">
            {t('user_generated_content.media.drag.drop_files_hint_files', {
              max: MAX_FILES,
              maxSizeMB: MAX_FILE_SIZE_MB,
            })}
          </span>
        </div>
      )}
      {children}
    </div>
  );
};

export default Dropzone;
